import { useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { AnimatedBasketball } from "./AnimatedBasketball";
import { BasketballCourt } from "./BasketballCourt";
import { normalizeToWorldSpace } from "./courtUtils";
import { HeatMapDot } from "./HeatMapDot";
import LogoMesh from "./LogoMesh";
import { ProgressBar } from "./ProgressBar";
import { StatsBar } from "./StatsBar3D";

/**
 * SharedAnimationScene - Reusable 3D scene for basketball shot animations
 *
 * This component contains all the common 3D elements used in both:
 * - The main display canvas (HeatMap.jsx)
 * - The recording canvas (RecordingScene.jsx)
 *
 * By extracting this into a shared component, we ensure that any changes
 * to the animation automatically apply to both the display and recording,
 * eliminating duplication and keeping them in perfect sync.
 *
 * Note: This component does NOT include:
 * - <Canvas> wrapper (managed by parent)
 * - Camera setup (managed by parent)
 * - Lighting (managed by parent, as they differ between display/recording)
 */
export const SharedAnimationScene = ({
	shots,
	animationState,
	restartKey = 0,
	onAnimationComplete,
	onStatsComplete, // New callback when stats fade-out completes
	statsAlwaysVisible = false, // for debugging, dead code in production
	completedCount = 0,
	statsDuration = 1500, // How long to show stats before fading out (ms)
}) => {
	// Time-based progress tracking for smooth, responsive progress bar
	const startTimeRef = useRef(null);
	const pausedTimeRef = useRef(0); // Track accumulated paused time
	const pauseStartRef = useRef(null);
	const [realTimeProgress, setRealTimeProgress] = useState(0);
	const [showStats, setShowStats] = useState(false);
	const [statsKey, setStatsKey] = useState(0);
	const statsShownTimeRef = useRef(null);
	const fadeOutTriggeredRef = useRef(false);

	// Reset timer and stats when animation restarts
	useEffect(() => {
		startTimeRef.current = null;
		pausedTimeRef.current = 0;
		pauseStartRef.current = null;
		setRealTimeProgress(0);
		setShowStats(false);
		statsShownTimeRef.current = null;
		fadeOutTriggeredRef.current = false;
	}, []);

	// Track pause/resume
	useEffect(() => {
		if (animationState === "paused" && pauseStartRef.current === null) {
			// Just paused
			pauseStartRef.current = performance.now();
		} else if (animationState === "playing" && pauseStartRef.current !== null) {
			// Just resumed
			const pauseDuration = performance.now() - pauseStartRef.current;
			pausedTimeRef.current += pauseDuration;
			pauseStartRef.current = null;
		} else if (animationState === "stopped") {
			// Reset everything
			startTimeRef.current = null;
			pausedTimeRef.current = 0;
			pauseStartRef.current = null;
			setRealTimeProgress(0);
		}
	}, [animationState]);

	// Calculate progress based on actual time elapsed
	useFrame(() => {
		if (animationState !== "playing" || shots.length === 0) return;

		const now = performance.now();

		if (startTimeRef.current === null) {
			startTimeRef.current = now;
		}

		// Elapsed time = current time - start time - total paused time
		const elapsed = now - startTimeRef.current - pausedTimeRef.current;

		// Total animation time: last shot starts at (shots.length - 1) * 400ms delay,
		// each shot lasts up to 1400ms (800ms flight + 600ms bounce/fall)
		const totalDuration = (shots.length - 1) * 400 + 1400; // in milliseconds

		const progress = Math.min(elapsed / totalDuration, 1);
		setRealTimeProgress(progress);
	});

	// Use time-based progress for smooth display
	const progress = realTimeProgress;

	useEffect(() => {
		if (!showStats && completedCount >= shots.length - 1 && shots.length > 0) {
			setShowStats(true);
			setStatsKey((prev) => prev + 1); // Force StatsBar remount to restart animations
		}
	}, [completedCount, shots.length, showStats]);

	return (
		<>
			{/* Professional Basketball Court */}
			<BasketballCourt />

			{/* Animated Basketballs */}
			{shots.map((shot, index) => (
				<AnimatedBasketball
					key={`animation-${shot.id || index}-${restartKey}`}
					shot={shot}
					delay={index * 0.4}
					animationState={animationState}
					onComplete={onAnimationComplete}
				/>
			))}

			{/* Made/Missed indicators on the ground */}
			{shots.map((shot, index) => {
				if (!shot.location) return null;
				// Normalize to world space (handles both old and new formats)
				const worldLocation = normalizeToWorldSpace(shot.location);
				return (
					<HeatMapDot
						key={`indicator-${shot.id || index}-${restartKey}`}
						position={[worldLocation.x, 0.1, worldLocation.z]}
						userData={shot}
					/>
				);
			})}

			{/* Progress Bar */}
			<ProgressBar progress={progress} />

			{showStats && <StatsBar key={statsKey} shots={shots} show={true} />}

			<LogoMesh />
		</>
	);
};
